<template>
    <svg :viewBox="`0 0 ${W} ${H}`" width="100%" class="summary-chart">
        <defs>
            <linearGradient id="sc-bar-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" :stop-color="c.barColor" stop-opacity="0.9" />
                <stop offset="100%" :stop-color="c.barColor" stop-opacity="0.28" />
            </linearGradient>
        </defs>

        <rect v-for="(zone, i) in c.zones" :key="`z${i}`" :x="zone.x" :y="zone.y" :width="zone.width"
            :height="zone.height" :fill="zone.color" />

        <rect :key="animKey" class="sc-bar" :x="c.barX + 6" :y="c.barTop" :width="barW - 12"
            :height="c.barBot - c.barTop" rx="3" fill="url(#sc-bar-grad)" />

        <template v-for="(ref, i) in c.refs">
            <line :key="`rl${i}`" :x1="c.barX - 14" :y1="ref.y" :x2="c.barX + barW + 14" :y2="ref.y" :stroke="ref.color"
                stroke-width="1.2" stroke-dasharray="3,2" opacity="0.85" />
            <text :key="`rt${i}`" :x="c.barX + barW + 18" :y="ref.y + 3.5" :fill="ref.color" font-size="8"
                font-weight="600">{{ ref.label }}</text>
        </template>

        <text :x="c.barX + barW / 2" :y="c.barTop - 6" text-anchor="middle" :fill="c.barColor" font-size="10"
            font-weight="800">{{ c.valueLabel }}</text>

        <template v-for="(col, i) in c.stats">
            <text :key="`sv${i}`" :x="col.x" :y="H - 14" text-anchor="middle" :class="{ 'sc-muted': col.muted }"
                :fill="col.muted ? undefined : col.color" font-size="9" font-weight="700">{{ col.value }}</text>
            <text :key="`sl${i}`" :x="col.x" :y="H - 4" text-anchor="middle" class="sc-muted" font-size="8">
                {{ col.label }}</text>
        </template>
    </svg>
</template>

<script>
export default {
    name: "EvaluationSummaryChart",
    props: {
        item: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            W: 340,
            H: 195,
            barW: 64,
            tPad: 18,
            bPad: 34,
        }
    },
    computed: {
        animKey(vm = this) {
            return vm.item ? vm.item.id : 'none'
        },
        c(vm = this) {
            const W = vm.W, barW = vm.barW, tPad = vm.tPad
            const barX = (W - barW) / 2
            const barH = vm.H - tPad - vm.bPad
            const item = vm.item || {}
            const ideal = parseFloat(item.ideal)
            const actual = parseFloat(item.actual)
            const lowerBetter = item.upgrade === false
            const scoreMin = 0.70, scoreMax = 1.20

            const score = v => {
                const val = parseFloat(v)
                if (!ideal || isNaN(ideal) || isNaN(val) || val === 0) return scoreMin
                return lowerBetter ? ideal / val : val / ideal
            }
            const sy = s => {
                const clamped = Math.max(scoreMin, Math.min(scoreMax, s))
                return tPad + barH * (1 - (clamped - scoreMin) / (scoreMax - scoreMin))
            }

            const zones = [
                { from: scoreMin, to: 0.90, color: 'rgba(239,68,68,0.10)' },
                { from: 0.90, to: 1.00, color: 'rgba(51,153,153,0.10)' },
                { from: 1.00, to: scoreMax, color: 'rgba(204,255,51,0.07)' },
            ].map(z => ({ x: barX, y: sy(z.to), width: barW, height: sy(z.from) - sy(z.to), color: z.color }))

            const lime = vm.$vuetify.theme.dark ? '#CCFF33' : '#6E9400'
            const sActual = score(actual)
            const barColor = sActual >= 1.0 ? lime : sActual >= 0.90 ? '#66CCCC' : '#F87171'

            const refs = [
                { s: 0.90, color: '#EF4444', label: vm.$t('common.low') },
                { s: 1.00, color: '#339999', label: vm.$t('common.ideal') },
                { s: 1.10, color: lime, label: vm.$t('common.optimal') },
            ].map(r => ({ y: sy(r.s), color: r.color, label: r.label }))

            const colW = W / 4
            const stats = [
                { label: vm.$t('common.best'), value: item.best, color: lime },
                { label: vm.$t('common.actual'), value: actual, color: barColor },
                { label: vm.$t('common.ideal'), value: ideal, color: '#66CCCC' },
                { label: vm.$t('common.average'), value: item.average, muted: true },
            ].map((col, i) => ({ ...col, x: colW * i + colW / 2 }))

            return {
                barX,
                barTop: sy(Math.max(sActual, scoreMin)),
                barBot: sy(scoreMin),
                barColor,
                zones,
                refs,
                stats,
                valueLabel: `${actual}${item.unit ? ' ' + item.unit : ''}`,
            }
        },
    },
}
</script>

<style scoped>
.summary-chart {
    display: block;
}

.sc-muted {
    fill: var(--v-txtMuted-base);
}

.sc-bar {
    transform-box: fill-box;
    transform-origin: bottom;
    animation: sc-grow 0.55s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes sc-grow {
    from {
        transform: scaleY(0);
    }

    to {
        transform: scaleY(1);
    }
}
</style>
